/**
 * 分割の予定（042、docs/30 §3）。案件の画面の見積の行の下に、その場で開く枠。
 *   ・回ごとに 名前・分け方（割合／税込の金額）・請求予定日。右に 税抜・消費税・税込（DB が計算した値をそのまま出す）
 *   ・打つたびに試算（POST …/billing-plan/preview）。下に合計と見積との差。説明は「？」
 *   ・請求書を起こした回は、名前と請求予定日だけ直せる（金額はその請求書で直す）
 */
import { INSTALLMENT_MSG, type BillingPlan, type InstallmentInput, type InstallmentRow } from "@genbaryoku/contract";
import { useEffect, useRef, useState } from "react";
import { api, messageOf } from "../api.js";
import { Help, ErrorText } from "./ui.jsx";
import { DateInput } from "./DateInput.jsx";
import { yen } from "../lib/num.js";

const toItems = (p: BillingPlan): InstallmentInput[] =>
  p.items.map((x) => ({ label: x.label, kind: x.kind, percent: x.percent, amountIncl: x.amountIncl, plannedOn: x.plannedOn }));

/** 全角の数字・記号を半角に（数字の欄の決めごと。docs/10 §5.6） */
const half = (v: string): string =>
  v.replace(/[０-９．]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0)).replace(/[,，\s円%％]/g, "");

export function BillingPlanEditor({
  quoteId,
  plan,
  draft,
  onSaved,
  onCancel,
  onDeleted,
}: {
  quoteId: string;
  /** 保存してある予定（新しく作るときは null） */
  plan: BillingPlan | null;
  /** 新しく作るときの既定（製品ごと：現場力＝着手金 30%・中間金 30%・完成時 残り、営業力＝前金・中間金・残金。defaultInstallments） */
  draft: InstallmentInput[];
  onSaved: (p: BillingPlan) => void;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const [items, setItems] = useState<InstallmentInput[]>(plan ? toItems(plan) : draft);
  const [calc, setCalc] = useState<BillingPlan | null>(plan);
  const [err, setErr] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  /** 大きいカレンダー（2026-09-23）。開いている回と、押したボタン */
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seqRef = useRef(0);

  // 打つたびに試算する（DB が計算する。画面は出すだけ）
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const mine = ++seqRef.current;
    timer.current = setTimeout(() => {
      void (async () => {
        try {
          const p = await api<BillingPlan>("POST", `/estimates/${quoteId}/billing-plan/preview`, { items });
          if (mine === seqRef.current) {
            setCalc(p);
            setErr(undefined);
          }
        } catch (e) {
          if (mine === seqRef.current) setErr(messageOf(e));
        }
      })();
    }, 250);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [items, quoteId]);

  const rows: (InstallmentRow | undefined)[] = items.map((_, i) => calc?.items[i]);
  const locked = (i: number): boolean => !!plan?.items[i]?.locked;
  const setItem = (i: number, p: Partial<InstallmentInput>) => setItems((xs) => xs.map((x, k) => (k === i ? { ...x, ...p } : x)));
  const add = () =>
    setItems((xs) => [...xs.slice(0, -1), { label: "中間金", kind: "percent", percent: 10, amountIncl: null, plannedOn: null }, xs[xs.length - 1]!]);
  const del = (i: number) => setItems((xs) => xs.filter((_, k) => k !== i));

  const save = async () => {
    setBusy(true);
    setErr(undefined);
    try {
      const p = await api<BillingPlan>("PUT", `/estimates/${quoteId}/billing-plan`, { items });
      onSaved(p);
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const drop = async () => {
    setBusy(true);
    setErr(undefined);
    try {
      await api<void>("DELETE", `/estimates/${quoteId}/billing-plan`);
      onDeleted();
    } catch (e) {
      setErr(messageOf(e));
      setBusy(false);
    }
  };

  const diff = calc?.diff ?? { subtotal: 0, tax: 0, total: 0 };
  const badRow = rows.some((r) => r?.error);
  const canSave = !busy && !calc?.error && !badRow && items.length >= 2 && items[items.length - 1]!.kind === "rest";

  return (
    <div className="plan" data-testid="plan-editor">
      <div className="row" style={{ alignItems: "center", marginBottom: 8 }}>
        <h2 style={{ margin: 0, fontSize: "var(--fs-17)" }}>
          分割の予定
          <Help
            testId="help-plan"
            text={
              <>
                割合の回は、税抜を10円単位（消費税が1円未満にならない単位。8% は25円）にそろえ、端数は最後の回に寄せます。これで税抜・消費税・税込とも見積と同じになります。
                <br />
                税込の金額で打った回は、その額ちょうどにできないとき1円少なくします。このとき消費税と税込の合計が1円ずれることがあります（税抜は同じ）。割合で分けると、ずれません。
                <br />
                請求書は予定の回の順に1枚ずつ起こします。起こした回の金額は、その請求書で修正します。
              </>
            }
          />
        </h2>
        {calc ? (
          <span className="muted">
            {calc.quoteNo ?? ""} {calc.subject}　ご契約金額（税込）<span className="num">{yen(calc.contract.total)}</span> 円
          </span>
        ) : null}
        <span className="sp" />
      </div>
      <div className="tbl-wrap">
        <table className="pl" data-testid="plan-table">
          <thead>
            <tr>
              <th>回</th>
              <th>名前</th>
              <th>分け方</th>
              <th>割合・金額</th>
              <th>請求予定日</th>
              <th className="amt">税抜</th>
              <th className="amt">消費税</th>
              <th className="amt">税込</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => {
              const r = rows[i];
              const last = i === items.length - 1;
              const lock = locked(i);
              return (
                <tr key={i} className={lock ? "locked" : undefined}>
                  <td className="seq">{i + 1}回目</td>
                  <td>
                    <input className="inp name" id={`pl_n${i}`} aria-label={`${i + 1}回目の名前`} value={it.label} onChange={(e) => setItem(i, { label: e.target.value })} />
                  </td>
                  <td>
                    {last ? (
                      <span>残り</span>
                    ) : lock ? (
                      <span>{it.kind === "percent" ? `割合 ${it.percent ?? 0}%` : `税込 ${yen(it.amountIncl ?? 0)} 円`}</span>
                    ) : (
                      <select
                        className="inp"
                        id={`pl_k${i}`}
                        aria-label={`${i + 1}回目の分け方`}
                        value={it.kind}
                        onChange={(e) => setItem(i, e.target.value === "percent" ? { kind: "percent", percent: 30, amountIncl: null } : { kind: "amount", percent: null, amountIncl: 1000000 })}
                      >
                        <option value="percent">割合</option>
                        <option value="amount">税込の金額</option>
                      </select>
                    )}
                  </td>
                  <td>
                    {last ? (
                      <span className="muted">見積の残り</span>
                    ) : lock ? (
                      <span className="muted">請求書 {plan?.items[i]?.invoice?.documentNo ?? "（下書き）"} を起こしました</span>
                    ) : (
                      <>
                        <span className="val">
                          <input
                            className="inp"
                            id={`pl_v${i}`}
                            inputMode="decimal"
                            aria-label={`${i + 1}回目の${it.kind === "percent" ? "割合" : "税込の金額"}`}
                            value={it.kind === "percent" ? (it.percent ?? "") : it.amountIncl == null ? "" : yen(it.amountIncl)}
                            onChange={(e) => {
                              const raw = half(e.target.value);
                              const v = raw === "" ? null : Number(raw);
                              setItem(i, it.kind === "percent" ? { percent: v } : { amountIncl: v == null ? null : Math.round(v) });
                            }}
                          />
                          <span>{it.kind === "percent" ? "%" : "円"}</span>
                        </span>
                        {r?.error ? <span className="rowerr">{r.error}</span> : null}
                        {r?.note ? <span className="rownote">{r.note}</span> : null}
                      </>
                    )}
                  </td>
                  <td>
                    {/* S3（★10）：日付の欄は DateInput（文字で打てる＋カレンダーの窓。マス 44px）。名前は「n回目の請求予定日」 */}
                    <DateInput
                      id={`pl_d${i}`}
                      className="pl-date"
                      label={`${i + 1}回目の請求予定日`}
                      ariaLabel={`${i + 1}回目の請求予定日`}
                      value={it.plannedOn ?? ""}
                      onChange={(v) => setItem(i, { plannedOn: v || null })}
                    />
                  </td>
                  <td className="amt num">{yen(r?.subtotal ?? 0)}</td>
                  <td className="amt num">{yen(r?.tax ?? 0)}</td>
                  <td className="amt num"><b>{yen(r?.total ?? 0)}</b></td>
                  <td>
                    {!last && !lock && items.length > 2 ? (
                      <button type="button" className="xbtn" aria-label={`${i + 1}回目を削除`} onClick={() => del(i)}>×</button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="sum">
              <td colSpan={5}>
                合計
                {/* 見積と合っているか（#171。前は表の下の1行「✓ ぴったり同じです」）：合計の横のチップ。合わないときは朱で差額（理由は title） */}
                {calc && !calc.error ? (
                  diff.total === 0 && diff.tax === 0 ? (
                    <span className="chip ai plchip" title={INSTALLMENT_MSG.exact.replace(/^✓ /, "")} data-testid="plan-verdict">{INSTALLMENT_MSG.matchChip}</span>
                  ) : (
                    <span className="chip shu plchip" title={INSTALLMENT_MSG.diff(diff.total)} data-testid="plan-verdict">{INSTALLMENT_MSG.diffChip(diff.total)}</span>
                  )
                ) : null}
              </td>
              <td className="amt num">{yen(calc?.sum.subtotal ?? 0)}</td>
              <td className="amt num">{yen(calc?.sum.tax ?? 0)}</td>
              <td className="amt num">{yen(calc?.sum.total ?? 0)}</td>
              <td />
            </tr>
            <tr className="quote">
              <td colSpan={5}>見積{calc?.quoteNo ? `（${calc.quoteNo}）` : ""}</td>
              <td className="amt num">{yen(calc?.contract.subtotal ?? 0)}</td>
              <td className="amt num">{yen(calc?.contract.tax ?? 0)}</td>
              <td className="amt num">{yen(calc?.contract.total ?? 0)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
      {/* 打った値が読めない（試算できない）ときは朱の1行（欄の入力の誤り＝通知にしない） */}
      <ErrorText>{calc?.error ?? err}</ErrorText>
      <div className="row" style={{ alignItems: "center", marginTop: 10 }}>
        <button type="button" className="small" disabled={busy || items.length >= 12} onClick={add} data-testid="plan-add">＋ 回追加</button>
        {plan?.canDelete ? (
          <button type="button" className="btn text" disabled={busy} onClick={() => void drop()} data-testid="plan-drop">分割解除</button>
        ) : null}
        <span className="sp" />
        <button type="button" className="btn quiet" disabled={busy} onClick={onCancel} data-testid="plan-cancel">取消</button>
        <button type="button" className="btn primary" disabled={!canSave} onClick={() => void save()} data-testid="plan-save">請求予定保存</button>
      </div>
    </div>
  );
}

/** 「●○○ 1／3回 請求済」 */
export function PlanProgress({ plan }: { plan: BillingPlan }) {
  const draftAt = plan.items.findIndex((x) => x.invoice?.status === "draft");
  return (
    <span className="progress" data-testid="plan-progress">
      <span className="dots" aria-hidden="true">
        {plan.items.map((_, i) => (
          <i key={i} className={i < plan.issuedCount ? "done" : i === draftAt ? "draft" : ""} />
        ))}
      </span>
      <span className="muted">
        {plan.issuedCount}／{plan.items.length}回 請求済
      </span>
    </span>
  );
}
